// R3 (helper) — overlay flattening for inline emphasis, adapted from lekterion's
// `src/read/runs.ts`. Each block's `text.slice(start, end)` is split into runs at
// emphasis boundaries so italic spans (`*Peter*` in the peter_pan rewrites)
// survive into the reader instead of flattening to plain text. Every run keeps
// its absolute `start` offset into the immutable `DocumentModel.text`, emitted as
// `data-start` so a later phase can map a DOM Selection back to document offsets
// (phase 6 gloss popover / phase 7 block selection).
//
// Our `DocumentModel` carries a single `emphasis` overlay (no bold/italic
// discriminator), so every emphasized run renders as `<em>`; the run structure
// is ready for a strong overlay if the model ever gains one.

import type { DocumentEmphasis } from "@bindery/shared/corpus/document";

/** A contiguous text run within a block; `em` marks an emphasized slice. */
export interface TextRun {
  start: number;
  value: string;
  em: boolean;
}

/**
 * Split `text[start, end)` into runs cut at every emphasis edge that falls
 * inside the block. Emphasis spans that only touch the block partially still
 * cut it at the boundary that lands inside.
 */
export function splitEmphasisRuns(
  text: string,
  start: number,
  end: number,
  emphasis: readonly DocumentEmphasis[],
): TextRun[] {
  const bounds = new Set<number>([start, end]);
  for (const e of emphasis) {
    if (e.start > start && e.start < end) bounds.add(e.start);
    if (e.end > start && e.end < end) bounds.add(e.end);
  }
  const cuts = [...bounds].sort((a, b) => a - b);

  const runs: TextRun[] = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    const from = cuts[i]!;
    const to = cuts[i + 1]!;
    if (to <= from) continue;
    const em = emphasis.some((e) => e.start <= from && e.end >= to);
    runs.push({ start: from, value: text.slice(from, to), em });
  }
  return runs;
}
